import _ from "lodash";
import client from "lib/apolloClient/client";
import { getConfigurationValuesQuery } from "Tooling/modules/ToolingQueries";
import {
  addConfigurationValuesMutationV2,
  internalToolsGenericMutation,
} from "Tooling/modules/ToolingMutations";
import { getInternalToolGenericDataQuery } from "InternalTools/modules/InternalToolsQuery";
import {
  REGION_NAME_BY_REGION,
  REGION_FILTER_OPTIONS,
} from "Platform/routes/Regions/modules/Module";
import {
  AUTH_AND_EVAL,
  WORKRAMP_ACADEMY,
  TODDLE_AI,
  WORKSHEET,
  WORKSHEET_AI,
  ADD_ON_MANAGER,
  ENABLE,
  DISABLE,
  CURRICULUM_PROGRAM,
  USER,
  TODDLE_LIBRARY,
  MESSAGING,
  GLOBAL,
  MESSAGING_MODULES,
  ACADEMIC_YEAR_TRANSITION,
  ORGANIZATION,
  ACTIVITY_TRACKER,
  BEHAVIOUR_MANAGEMENT,
  STUDENT_PRONOUNS,
  IN_APP_TRANSLATION,
  DP_1_AND_DP_2_STUDENTS,
  IBIS_REGISTRATION_REVAMP,
  ADD_ADMIN_TO_ALL_CLASSES,
  TODDLE_ANALYTICS,
  TODDLE_ANALYTICS_MODULES,
  CROSS_CURRICULUM_STUDENTS,
  AI_TUTOR,
  ROLES_AND_PERMISSIONS,
  UNIT_PLANNING_AI,
  AI_GRADING,
  IMPORT_UNIT_FROM_OTHER_CURRICULUM,
  BLUEPRINT_SYNC,
  ROSTER_SYNC,
  TIMETABLE_SYNC,
  ATTENDANCE_SYNC,
} from "./OrgAddOnContants";
import { AUTH_AND_EVAL as AUTH_AND_EVAL_V2 } from "InternalTools/components/AddOnManager/modules/AddOnManagerConstants";
import {
  ADD_ON_AI_TUTOR,
  ENABLE_ADD_ON_IBIS_REVAMP,
  ENABLE_ADD_ON_ROSTER_SYNC,
  ENABLE_ADD_ON_TIMETABLE_ATTENDANCE_SYNC,
} from "Constants/permissionConstants";
import { CURRICULUM_PLAN_ID_MAP } from "Constants";

const ORGANIZATION_LEVEL = "ORGANIZATION_LEVEL";
const CURRICULUM_LEVEL = "CURRICULUM_LEVEL";

export const getQueryVariablesForMessaging = _.memoize(orgId => ({
  input: {
    apiType: "ADD_ON_MANAGER",
    params: {
      organizationId: orgId,
      addOnType: MESSAGING,
    },
  },
}));

const MESSAGING_MODULES_LIST = [
  MESSAGING_MODULES.PAID_MODULE,
  MESSAGING_MODULES.CHATS_AND_CHANNELS,
  MESSAGING_MODULES.OVERVIEW,
  MESSAGING_MODULES.CHAT_CHANNELS_AI_ADD_ONS,
  MESSAGING_MODULES.OVERVIEW_WITH_AI,
  MESSAGING_MODULES.SETTINGS,
];

export const ORG_ADD_ONS_MAPPING = [
  {
    id: WORKRAMP_ACADEMY,
    label: "WorkRamp Academy",
    description: "Decide if workramp academy is enabled.",
    perm: "FeatureFlag:AddOnWorkrampAcademy",
    isConfigurable: true,
    addOnsParameters: {
      valueType: "BOOLEAN",
      entityType: "ORGANIZATION",
      configurationId: "4879",
    },
    getAddOnStatusFn: async ({ addOnsParameters, orgId, addOnId }) => {
      const data = await getWorkrampAcademyStatusForOrg({
        addOnsParameters,
        orgId,
        addOnId,
      });
      return data;
    },
  },
  {
    id: AUTH_AND_EVAL,
    label: "Authorization and Evaluation",
    description: "Manage access for Authorization and Evaluation",
    perm: "FeatureFlag:AddOnSnP",
    isConfigurable: true,
    addOnsParameters: {
      apiType: "FETCH_CURRICULUM_PROGRAM_WITH_SNP_PERM_STATUS",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await checkIfAuthAndEvalEnabled({ variables });
      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );
      const isChecked = _.some(curriculumPrograms, { snpEnabled: true });
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: TODDLE_LIBRARY,
    label: "Toddle Library",
    description: "Manage access for Toddle Library",
    perm: "FeatureFlag:AddOnToddleLibrary",
    isConfigurable: true,
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
      addOnType: "LIBRARY",
      queryType: "FETCH_LIBRARY_PORTAL_ACTIVATION_STATUS",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });

      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );
      const isChecked = _.some(curriculumPrograms, { libraryEnabled: true });

      return {
        checked: isChecked,
      };
    },
  },
  {
    id: TODDLE_ANALYTICS,
    label: "Toddle Insights",
    description: "Manage access for Toddle Insights",
    perm: "Analytics:AddOnInsights",
    isConfigurable: true,
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
      addOnType: "TODDLE_ANALYTICS",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });

      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );

      const isChecked = _.some(curriculumPrograms, curriculumProgram => {
        const status = _.get(curriculumProgram, "status", {});

        const isEnabledForCurriculum = _.reduce(
          TODDLE_ANALYTICS_MODULES,
          (curriculumStatus, module) => {
            const moduleId = _.get(module, "id");
            const moduleStatus = _.get(status, `${moduleId}_ANALYTICS`, false);
            return curriculumStatus || moduleStatus;
          },
          false
        );
        return isEnabledForCurriculum;
      });

      return {
        checked: isChecked,
      };
    },
  },
  {
    id: TODDLE_AI,
    useLocale: true,
    label: "common:toddle_ai",
    description: "ai:manage_access_for_toddle_ai_features",
    perm: "FeatureFlag:AddOnToddleAI",
    isConfigurable: true,
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await checkIfAddOnEnabled({ variables });
      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );
      const isChecked = _.some(curriculumPrograms, curriculumProgram => {
        const status = _.get(curriculumProgram, "status", {});
        const enabledUsers = _.get(curriculumProgram, "enabledUsers", {});
        const isCurruiculumLevelEnabled = _.some(status, feature => feature);
        const isUserLevelEnabled = _.some(
          enabledUsers,
          usersArr => !_.isEmpty(usersArr)
        );
        return isCurruiculumLevelEnabled || isUserLevelEnabled;
      });
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: AI_TUTOR,
    useLocale: true,
    label: "common:ai_tutor",
    description: "ai:manage_access_for_ai_tutor_features",
    perm: ADD_ON_AI_TUTOR,
    isConfigurable: true,
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await checkIfAddOnEnabled({ variables });
      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );
      const isChecked = _.some(curriculumPrograms, curriculumProgram => {
        const status = _.get(curriculumProgram, "status", false);
        const hasEnabledUsers = !_.isEmpty(
          _.get(curriculumProgram, "enabledUsers")
        );
        return status || hasEnabledUsers;
      });
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: WORKSHEET,
    useLocale: true,
    label: "common:worksheet",
    description: "worksheet:manage_access_for_worksheet",
    perm: "FeatureFlag:AddOnWorksheet",
    isConfigurable: true,
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await checkIfAddOnEnabled({ variables });
      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );
      const isChecked = _.some(curriculumPrograms, {
        status: { WORKSHEET_BASIC: true },
      });
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: AI_GRADING,
    useLocale: true,
    label: "common:ai_grading",
    description: "common:manage_access_for_ai_grading",
    perm: "FeatureFlag:AddOnAIGrading",
    isConfigurable: true,
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await checkIfAddOnEnabled({ variables });
      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );
      const isChecked = _.some(curriculumPrograms, {
        status: { AI_GRADING: true },
      });
      return {
        checked: isChecked,
      };
    },
  },
  // {
  //   id: UNIT_PLANNING_AI,
  //   useLocale: true,
  //   label: "common:unit_planning_ai",
  //   description: "common:manage_access_for_unit_planning_ai",
  //   perm: "FeatureFlag:AddOnUnitPlanningAI",
  //   isConfigurable: true,
  //   addOnsParameters: {
  //     apiType: "ADD_ON_MANAGER",
  //   },
  //   getAddOnStatusFn: async params => {
  //     const { variables } = getVariablesForAddOnStatus(params);
  //     const data = await checkIfAddOnEnabled({ variables });
  //     const curriculumPrograms = _.get(
  //       data,
  //       "platform.internalToolsGenericQuery.data.curriculumPrograms",
  //       []
  //     );
  //     const isChecked = _.some(curriculumPrograms, {
  //       status: { UNIT_PLANNING_AI: true },
  //     });
  //     return {
  //       checked: isChecked,
  //     };
  //   },
  // },
  {
    id: MESSAGING,
    useLocale: true,
    label: "common:messaging",
    description: "common:manage_access_for_messaging_features",
    perm: "FeatureFlag:AddOnToddleMessaging",
    isConfigurable: true,
    addOnsParameters: { apiType: "ADD_ON_MANAGER" },
    getAddOnStatusFn: async params => {
      const { orgId } = params;

      const variables = getQueryVariablesForMessaging(orgId);
      const cachedData = await checkIfAddOnEnabled({ variables });

      const data = _.get(
        cachedData,
        "platform.internalToolsGenericQuery.data",
        {}
      );

      if (_.isEmpty(data)) return { checked: false };

      const { enabledUsers } = data;

      const isAnythingEnabled = _.some(MESSAGING_MODULES_LIST, moduleId => {
        const isModuleEnabled = _.get(data, moduleId, false);
        const isUserLevelEnabled = !_.isEmpty(
          _.get(enabledUsers, moduleId, [])
        );
        return isModuleEnabled || isUserLevelEnabled;
      });

      return { checked: isAnythingEnabled };
    },
  },
  {
    id: ACADEMIC_YEAR_TRANSITION,
    label: "Academic Year Transition",
    description: "Enable/disable Academic Year Transition flow",
    perm: "FeatureFlag:AddOnManager",
    isConfigurable: true,
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
      addOnType: "ACADEMIC_YEAR_TRANSITION",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });
      const checked = _.get(
        data,
        "platform.internalToolsGenericQuery.data[0].value[0].value",
        false
      );

      const isChecked = checked === "true";
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: ACTIVITY_TRACKER,
    useLocale: true,
    label: "journal:activity_tracker",
    description: "journal:manage_access_for_activity_tracker",
    perm: "FeatureFlag:AddOnManager",
    isConfigurable: true,
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await checkIfAddOnEnabled({ variables });
      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );

      const isChecked = _.some(curriculumPrograms, {
        isEnabled: true,
      });
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: BEHAVIOUR_MANAGEMENT,
    useLocale: true,
    label: "behaviour:behaviour_management",
    perm: "FeatureFlag:AddOnBehaviour",
    isConfigurable: true,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: BEHAVIOUR_MANAGEMENT,
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      const isChecked = _.some(queryData, org_data => {
        return _.some(org_data.value, ({ value }) => value === "true");
      });

      return {
        checked: isChecked,
      };
    },
  },
  {
    id: STUDENT_PRONOUNS,
    useLocale: true,
    label: "common:student_pronouns",
    perm: "FeatureFlag:AddOnStudentPronouns",
    isConfigurable: true,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: STUDENT_PRONOUNS,
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      const isChecked = _.some(queryData, org_data => {
        return _.some(org_data.value, ({ value }) => value === "true");
      });

      return {
        checked: isChecked,
      };
    },
  },
  {
    id: IN_APP_TRANSLATION,
    useLocale: true,
    label: "journal:in_app_translation",
    description: "journal:manage_access_for_in_app_translation",
    perm: "FeatureFlag:AddOnManager",
    isConfigurable: true,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: IN_APP_TRANSLATION,
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await checkIfAddOnEnabled({ variables });
      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      const isChecked = _.some(queryData, {
        value: true,
      });
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: DP_1_AND_DP_2_STUDENTS,
    label: "DP 1 and DP 2 Students",
    description: "Allow DP 1 and DP 2 Students permission",
    isConfigurable: true,
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
      addOnType: "DP_1_AND_DP_2_STUDENTS",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });
      const checked = _.get(
        data,
        "platform.internalToolsGenericQuery.data[0].value[0].value",
        false
      );
      const isChecked = checked === "true";
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: IBIS_REGISTRATION_REVAMP,
    useLocale: true,
    label: "common:ibis_registration_revamp",
    description: "common:ibis_registration_revamp_description",
    perm: ENABLE_ADD_ON_IBIS_REVAMP,
    isConfigurable: true,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: IBIS_REGISTRATION_REVAMP,
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });
      const isIBISRevampEnabled = _.get(
        data,
        "platform.internalToolsGenericQuery.data.permissionValue",
        false
      );
      return { checked: isIBISRevampEnabled };
    },
  },
  {
    id: ADD_ADMIN_TO_ALL_CLASSES,
    label: "Add Admin to all classes",
    isConfigurable: true,
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
      addOnType: "ADD_ADMIN_TO_ALL_CLASSES",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });
      const checked = _.get(
        data,
        "platform.internalToolsGenericQuery.data[0].value[0].value",
        false
      );

      const isChecked = checked === "true";
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: CROSS_CURRICULUM_STUDENTS,
    useLocale: false,
    label: "Cross Curriculum Students",
    isConfigurable: true,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: CROSS_CURRICULUM_STUDENTS,
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      const isChecked = _.some(queryData, org_data => {
        return _.some(org_data.value, ({ value }) => value === "true");
      });
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: ROLES_AND_PERMISSIONS,
    useLocale: true,
    label: "common:roles_and_permissions",
    perm: "FeatureFlag:AddOnForRolesAndPermissions",
    isConfigurable: true,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: ROLES_AND_PERMISSIONS,
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);

      const data = await getInternalToolGenericData({ variables });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      // Show checked if both the configuration and consumption are enabled
      const isChecked =
        _.size(queryData) == 2 &&
        _.every(queryData, org_data => {
          return _.every(org_data?.value, ({ value }) => value == "true");
        });
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: BLUEPRINT_SYNC,
    useLocale: true,
    label: "common:blueprint_sync",
    perm: "FeatureFlag:AddOnForBluePrintSync",
    isConfigurable: true,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: BLUEPRINT_SYNC,
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      // Show checked if both the configuration and consumption are enabled
      const isChecked = _.some(queryData, org_data => {
        return _.isEqual(org_data.value, true);
      });
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: ROSTER_SYNC,
    useLocale: true,
    label: "integration:roster_sync",
    description: "Manage access for roster sync",
    isConfigurable: true,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: ROSTER_SYNC,
    },
    perm: ENABLE_ADD_ON_ROSTER_SYNC,
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );
      const isChecked = _.get(queryData, "showSyncLinkV2", false);

      return { checked: isChecked };
    },
  },
  {
    id: TIMETABLE_SYNC,
    useLocale: true,
    label: "integration:timetable_sync",
    description: "Manage access for timetable sync",
    isConfigurable: true,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: TIMETABLE_SYNC,
    },
    perm: ENABLE_ADD_ON_TIMETABLE_ATTENDANCE_SYNC,
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      // check if the module is enabled or not
      const isChecked = _.get(queryData, "syncModule", false);

      return { checked: isChecked };
    },
  },
  {
    id: ATTENDANCE_SYNC,
    useLocale: true,
    label: "integration:attendance_sync",
    description: "Manage access for attendance sync",
    isConfigurable: true,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: ATTENDANCE_SYNC,
    },
    perm: ENABLE_ADD_ON_TIMETABLE_ATTENDANCE_SYNC,
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      // check if the module is enabled or not
      const isChecked = _.get(queryData, "syncModule", false);

      return { checked: isChecked };
    },
  },
];

const {
  PRO_PILOT,
  PRO,
  ESSENTIALS_PILOT,
  ESSENTIALS,
  ULTIMATE_PILOT,
  ULTIMATE,
  ADVANCE_PILOT,
  ADVANCE,
} = CURRICULUM_PLAN_ID_MAP;

export const ORG_ADD_ONS_MAPPING_V2 = [
  {
    id: WORKRAMP_ACADEMY,
    label: "WorkRamp Academy",
    description: "Decide if workramp academy is enabled.",
    perm: "FeatureFlag:AddOnWorkrampAcademy",
    isConfigurable: true,
    addOnType: ORGANIZATION_LEVEL,
    enabledWithPlans: [
      PRO_PILOT,
      PRO,
      ESSENTIALS_PILOT,
      ESSENTIALS,
      ULTIMATE_PILOT,
      ULTIMATE,
      ADVANCE_PILOT,
      ADVANCE,
    ],
    needsConfiguration: true, // Indicating that this add-on needs additional configuration if pre-enabled due to plan
    addOnsParameters: {
      valueType: "BOOLEAN",
      entityType: "ORGANIZATION",
      configurationId: "4879",
    },
    getAddOnStatusFn: async ({
      addOnsParameters,
      orgId,
      addOnId,
      isInitialFetch = false,
    }) => {
      const data = await getWorkrampAcademyStatusForOrg({
        addOnsParameters,
        orgId,
        addOnId,
        isInitialFetch,
      });
      return data;
    },
  },
  {
    id: AUTH_AND_EVAL_V2,
    label: "Authorization and Evaluation",
    description: "Manage access for Authorization and Evaluation",
    perm: "FeatureFlag:AddOnSnP",
    isConfigurable: true,
    addOnType: CURRICULUM_LEVEL,
    enabledWithPlans: [ULTIMATE_PILOT, ULTIMATE],
    needsConfiguration: true, // Indicating that this add-on needs additional configuration if pre-enabled due to plan
    addOnsParameters: {
      apiType: "FETCH_CURRICULUM_PROGRAM_WITH_SNP_PERM_STATUS",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await checkIfAuthAndEvalEnabled({
        variables,
        isInitialFetch,
      });
      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );
      const updatedCurriculums = _.map(curriculumPrograms, curriculum => {
        return {
          id: _.get(curriculum, "id"),
          uid: _.get(curriculum, "uid"),
          checked: curriculum.snpEnabled ? true : false,
        };
      });
      return { curriculumStatus: updatedCurriculums };
    },
  },
  {
    id: TODDLE_LIBRARY,
    label: "Toddle Library",
    description: "Manage access for Toddle Library",
    perm: "FeatureFlag:AddOnToddleLibrary",
    isConfigurable: true,
    addOnType: CURRICULUM_LEVEL,
    enabledWithPlans: [ULTIMATE_PILOT, ULTIMATE],
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
      addOnType: "LIBRARY",
      queryType: "FETCH_LIBRARY_PORTAL_ACTIVATION_STATUS",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await getInternalToolGenericData({
        variables,
        isInitialFetch,
      });

      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );

      const updatedCurriculums = _.map(curriculumPrograms, curriculum => {
        const curriculumId = _.get(curriculum, "id");

        return {
          id: curriculumId,
          uid: _.get(curriculum, "uid"),
          checked: curriculum.libraryEnabled ? true : false,
        };
      });

      return { curriculumStatus: updatedCurriculums };
    },
  },
  {
    id: TODDLE_ANALYTICS,
    label: "Toddle Insights",
    description: "Manage access for Toddle Insights",
    perm: "Analytics:AddOnInsights",
    isConfigurable: true,
    addOnType: CURRICULUM_LEVEL,
    enabledWithPlans: [
      ESSENTIALS_PILOT,
      ESSENTIALS,
      ULTIMATE_PILOT,
      ULTIMATE,
      ADVANCE_PILOT,
      ADVANCE,
    ],
    needsConfiguration: true, // Indicating that this add-on needs additional configuration if pre-enabled due to plan
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
      addOnType: "TODDLE_ANALYTICS",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await getInternalToolGenericData({
        variables,
        isInitialFetch,
      });

      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );

      const updatedCurriculums = _.map(curriculumPrograms, curriculum => {
        const status = _.get(curriculum, "status", {});
        const enabledModulesSet = new Set();

        const isChecked = _.reduce(
          TODDLE_ANALYTICS_MODULES,
          (curriculumStatus, module) => {
            const moduleId = _.get(module, "id");
            const moduleStatus = _.get(status, `${moduleId}_ANALYTICS`, false);
            if (moduleStatus) enabledModulesSet.add(moduleId);
            return curriculumStatus || moduleStatus;
          },
          false
        );

        return {
          id: _.get(curriculum, "id"),
          uid: _.get(curriculum, "uid"),
          checked: isChecked,
          enabledModules: Array.from(enabledModulesSet),
        };
      });

      return { curriculumStatus: updatedCurriculums };
    },
  },
  {
    id: TODDLE_AI,
    useLocale: true,
    label: "common:toddle_ai",
    description: "ai:manage_access_for_toddle_ai_features",
    perm: "FeatureFlag:AddOnToddleAI",
    isConfigurable: true,
    addOnType: CURRICULUM_LEVEL,
    enabledWithPlans: [ULTIMATE_PILOT, ULTIMATE, ADVANCE_PILOT, ADVANCE],
    needsConfiguration: true, // Indicating that this add-on needs additional configuration if pre-enabled due to plan
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await checkIfAddOnEnabled({ variables, isInitialFetch });
      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );

      const updatedCurriculums = _.map(curriculumPrograms, curriculum => {
        const status = _.get(curriculum, "status", {});
        const enabledModulesSet = new Set();
        const enabledUsers = _.get(curriculum, "enabledUsers", {});
        const isCurruiculumLevelEnabled = _.some(status, (value, key) => {
          if (value) enabledModulesSet.add(key);
          return value;
        });
        const isUserLevelEnabled = _.some(
          enabledUsers,
          usersArr => !_.isEmpty(usersArr)
        );
        return {
          id: _.get(curriculum, "id"),
          uid: _.get(curriculum, "uid"),
          checked: isCurruiculumLevelEnabled || isUserLevelEnabled,
          enabledModules: Array.from(enabledModulesSet),
        };
      });
      return { curriculumStatus: updatedCurriculums };
    },
  },
  {
    id: AI_TUTOR,
    useLocale: true,
    label: "common:ai_tutor",
    description: "ai:manage_access_for_ai_tutor_features",
    perm: ADD_ON_AI_TUTOR,
    isConfigurable: true,
    addOnType: CURRICULUM_LEVEL,
    enabledWithPlans: [ULTIMATE_PILOT, ULTIMATE],
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await checkIfAddOnEnabled({ variables, isInitialFetch });
      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );
      const updatedCurriculums = _.map(curriculumPrograms, curriculum => {
        const status = _.get(curriculum, "status", false);
        const hasEnabledUsers = !_.isEmpty(_.get(curriculum, "enabledUsers"));
        return {
          id: _.get(curriculum, "id"),
          uid: _.get(curriculum, "uid"),
          checked: status || hasEnabledUsers,
        };
      });
      return { curriculumStatus: updatedCurriculums };
    },
  },
  {
    id: WORKSHEET,
    useLocale: true,
    label: "common:worksheet",
    description: "worksheet:manage_access_for_worksheet",
    perm: "FeatureFlag:AddOnWorksheet",
    isConfigurable: false,
    addOnType: CURRICULUM_LEVEL,
    enabledWithPlans: [ULTIMATE_PILOT, ULTIMATE, ADVANCE_PILOT, ADVANCE],
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await checkIfAddOnEnabled({ variables, isInitialFetch });
      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );
      const updatedCurriculums = _.map(curriculumPrograms, curriculum => {
        return {
          id: _.get(curriculum, "id"),
          uid: _.get(curriculum, "uid"),
          checked: _.get(curriculum, "status.WORKSHEET_BASIC", false),
        };
      });
      return { curriculumStatus: updatedCurriculums };
    },
  },
  {
    id: WORKSHEET_AI,
    useLocale: true,
    label: "worksheet:worksheet_ai",
    description: "worksheet:manage_access_for_worksheet_ai",
    perm: "FeatureFlag:WorksheetAIAddon",
    isConfigurable: false,
    addOnType: CURRICULUM_LEVEL,
    enabledWithPlans: [ULTIMATE_PILOT, ULTIMATE, ADVANCE_PILOT, ADVANCE],
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await checkIfAddOnEnabled({ variables, isInitialFetch });
      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );
      const updatedCurriculums = _.map(curriculumPrograms, curriculum => {
        return {
          id: _.get(curriculum, "id"),
          uid: _.get(curriculum, "uid"),
          checked: _.get(curriculum, "status.WORKSHEET_AI", false),
        };
      });
      return { curriculumStatus: updatedCurriculums };
    },
  },
  {
    id: AI_GRADING,
    useLocale: true,
    label: "common:ai_grading",
    description: "common:manage_access_for_ai_grading",
    perm: "FeatureFlag:AddOnAIGrading",
    isConfigurable: false,
    addOnType: CURRICULUM_LEVEL,
    enabledWithPlans: [ULTIMATE_PILOT, ULTIMATE],
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await checkIfAddOnEnabled({ variables, isInitialFetch });
      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );
      const updatedCurriculums = _.map(curriculumPrograms, curriculum => {
        return {
          id: _.get(curriculum, "id"),
          uid: _.get(curriculum, "uid"),
          checked: _.get(curriculum, "status.AI_GRADING", false),
        };
      });
      return { curriculumStatus: updatedCurriculums };
    },
  },
  {
    id: IMPORT_UNIT_FROM_OTHER_CURRICULUM,
    useLocale: true,
    label: "common:import_unit_from_other_curriculum",
    description: "common:manage_access_for_import_unit_from_other_curriculum",
    perm: "FeatureFlag:AllowOtherCurriculumUnitImport",
    isConfigurable: false,
    addOnType: CURRICULUM_LEVEL,
    enabledWithPlans: [ULTIMATE_PILOT, ULTIMATE],
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await checkIfAddOnEnabled({ variables, isInitialFetch });
      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );
      const updatedCurriculums = _.map(curriculumPrograms, curriculum => {
        return {
          id: _.get(curriculum, "id"),
          uid: _.get(curriculum, "uid"),
          checked: _.get(
            curriculum,
            "status.IMPORT_OTHER_CURRICULUM_UNIT",
            false
          ),
        };
      });
      return { curriculumStatus: updatedCurriculums };
    },
  },
  // {
  //   id: UNIT_PLANNING_AI,
  //   useLocale: true,
  //   label: "common:unit_planning_ai",
  //   description: "common:manage_access_for_unit_planning_ai",
  //   perm: "FeatureFlag:AddOnUnitPlanningAI",
  //   isConfigurable: true,
  //   addOnsParameters: {
  //     apiType: "ADD_ON_MANAGER",
  //   },
  //   getAddOnStatusFn: async params => {
  //     const { variables } = getVariablesForAddOnStatus(params);
  //     const data = await checkIfAddOnEnabled({ variables });
  //     const curriculumPrograms = _.get(
  //       data,
  //       "platform.internalToolsGenericQuery.data.curriculumPrograms",
  //       []
  //     );
  //     const isChecked = _.some(curriculumPrograms, {
  //       status: { UNIT_PLANNING_AI: true },
  //     });
  //     return {
  //       checked: isChecked,
  //     };
  //   },
  // },
  {
    id: MESSAGING,
    useLocale: true,
    label: "common:messaging",
    description: "common:manage_access_for_messaging_features",
    perm: "FeatureFlag:AddOnToddleMessaging",
    isConfigurable: true,
    addOnType: ORGANIZATION_LEVEL,
    enabledWithPlans: [ULTIMATE_PILOT, ULTIMATE, ADVANCE_PILOT, ADVANCE],
    needsConfiguration: true, // Indicating that this add-on needs additional configuration if pre-enabled due to plan
    addOnsParameters: { apiType: "ADD_ON_MANAGER" },
    getAddOnStatusFn: async params => {
      const { orgId } = params;
      const isInitialFetch = params.isInitialFetch || false;

      const variables = getQueryVariablesForMessaging(orgId);
      const cachedData = await checkIfAddOnEnabled({
        variables,
        isInitialFetch,
      });

      const data = _.get(
        cachedData,
        "platform.internalToolsGenericQuery.data",
        {}
      );

      if (_.isEmpty(data)) return { checked: false };

      const { enabledUsers } = data;

      const enabledModules = _.filter(MESSAGING_MODULES_LIST, moduleId => {
        const isModuleEnabled = _.get(data, moduleId, false);
        const isUserLevelEnabled = !_.isEmpty(
          _.get(enabledUsers, moduleId, [])
        );
        return isModuleEnabled || isUserLevelEnabled;
      });

      const isAnythingEnabled = !_.isEmpty(enabledModules);

      return {
        checked: isAnythingEnabled,
        enabledModules,
      };
    },
  },
  {
    id: ACADEMIC_YEAR_TRANSITION,
    label: "Academic Year Transition",
    description: "Enable/disable Academic Year Transition flow",
    perm: "FeatureFlag:AddOnManager",
    isConfigurable: false,
    addOnType: ORGANIZATION_LEVEL,
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
      addOnType: "ACADEMIC_YEAR_TRANSITION",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await getInternalToolGenericData({
        variables,
        isInitialFetch,
      });
      const checked = _.get(
        data,
        "platform.internalToolsGenericQuery.data[0].value[0].value",
        false
      );

      const isChecked = checked === "true";
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: ACTIVITY_TRACKER,
    useLocale: true,
    label: "journal:activity_tracker",
    description: "journal:manage_access_for_activity_tracker",
    perm: "FeatureFlag:AddOnManager",
    isConfigurable: false,
    addOnType: CURRICULUM_LEVEL,
    enabledWithPlans: [ULTIMATE_PILOT, ULTIMATE],
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await checkIfAddOnEnabled({ variables, isInitialFetch });
      const curriculumPrograms = _.get(
        data,
        "platform.internalToolsGenericQuery.data.curriculumPrograms",
        []
      );

      const updatedCurriculums = _.map(curriculumPrograms, curriculum => {
        return {
          id: _.get(curriculum, "id"),
          uid: _.get(curriculum, "uid"),
          checked: _.get(curriculum, "isEnabled", false),
        };
      });
      return { curriculumStatus: updatedCurriculums };
    },
  },
  {
    id: BEHAVIOUR_MANAGEMENT,
    useLocale: true,
    label: "behaviour:behaviour_management",
    perm: "FeatureFlag:AddOnBehaviour",
    isConfigurable: false,
    addOnType: ORGANIZATION_LEVEL,
    enabledWithPlans: [ULTIMATE_PILOT, ULTIMATE],
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: BEHAVIOUR_MANAGEMENT,
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await getInternalToolGenericData({
        variables,
        isInitialFetch,
      });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      const isChecked = _.some(queryData, org_data => {
        return _.some(org_data.value, ({ value }) => value === "true");
      });

      return {
        checked: isChecked,
      };
    },
  },
  {
    id: STUDENT_PRONOUNS,
    useLocale: true,
    label: "common:student_pronouns",
    perm: "FeatureFlag:AddOnStudentPronouns",
    isConfigurable: false,
    addOnType: ORGANIZATION_LEVEL,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: STUDENT_PRONOUNS,
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await getInternalToolGenericData({
        variables,
        isInitialFetch,
      });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      const isChecked = _.some(queryData, org_data => {
        return _.some(org_data.value, ({ value }) => value === "true");
      });

      return {
        checked: isChecked,
      };
    },
  },
  {
    id: IN_APP_TRANSLATION,
    useLocale: true,
    label: "journal:in_app_translation",
    description: "journal:manage_access_for_in_app_translation",
    perm: "FeatureFlag:AddOnManager",
    isConfigurable: false,
    addOnType: ORGANIZATION_LEVEL,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: IN_APP_TRANSLATION,
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await checkIfAddOnEnabled({ variables, isInitialFetch });
      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      const isChecked = _.some(queryData, {
        value: true,
      });
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: DP_1_AND_DP_2_STUDENTS,
    label: "DP 1 and DP 2 Students",
    description: "Allow DP 1 and DP 2 Students permission",
    isConfigurable: false,
    addOnType: ORGANIZATION_LEVEL,
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
      addOnType: "DP_1_AND_DP_2_STUDENTS",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await getInternalToolGenericData({
        variables,
        isInitialFetch,
      });
      const checked = _.get(
        data,
        "platform.internalToolsGenericQuery.data[0].value[0].value",
        false
      );
      const isChecked = checked === "true";
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: IBIS_REGISTRATION_REVAMP,
    useLocale: true,
    label: "common:ibis_registration_revamp",
    description: "common:ibis_registration_revamp_description",
    perm: ENABLE_ADD_ON_IBIS_REVAMP,
    isConfigurable: true,
    addOnType: ORGANIZATION_LEVEL,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: IBIS_REGISTRATION_REVAMP,
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await getInternalToolGenericData({
        variables,
        isInitialFetch,
      });
      const isIBISRevampEnabled = _.get(
        data,
        "platform.internalToolsGenericQuery.data.permissionValue",
        false
      );
      return { checked: isIBISRevampEnabled };
    },
  },
  {
    id: ADD_ADMIN_TO_ALL_CLASSES,
    label: "Add Admin to all classes",
    isConfigurable: false,
    addOnType: ORGANIZATION_LEVEL,
    addOnsParameters: {
      apiType: "ADD_ON_MANAGER",
      addOnType: "ADD_ADMIN_TO_ALL_CLASSES",
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await getInternalToolGenericData({
        variables,
        isInitialFetch,
      });
      const checked = _.get(
        data,
        "platform.internalToolsGenericQuery.data[0].value[0].value",
        false
      );

      const isChecked = checked === "true";
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: CROSS_CURRICULUM_STUDENTS,
    useLocale: false,
    label: "Cross Curriculum Students",
    isConfigurable: false,
    addOnType: ORGANIZATION_LEVEL,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: CROSS_CURRICULUM_STUDENTS,
    },
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await getInternalToolGenericData({
        variables,
        isInitialFetch,
      });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      const isChecked = _.some(queryData, org_data => {
        return _.some(org_data.value, ({ value }) => value === "true");
      });
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: ROLES_AND_PERMISSIONS,
    useLocale: true,
    label: "common:roles_and_permissions",
    perm: "FeatureFlag:AddOnForRolesAndPermissions",
    addOnType: ORGANIZATION_LEVEL,
    enabledWithPlans: [], // Keeping plans as EMPTY as this add-on enabled by default for all orgs
    isConfigurable: true,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: ROLES_AND_PERMISSIONS,
    },
    needsConfiguration: true, // Indicating that this add-on needs additional configuration if pre-enabled due to plan
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;

      const data = await getInternalToolGenericData({
        variables,
        isInitialFetch,
      });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      // Show checked if both the configuration and consumption are enabled
      const isChecked =
        _.size(queryData) == 2 &&
        _.every(queryData, org_data => {
          return _.every(org_data?.value, ({ value }) => value == "true");
        });
      return {
        checked: isChecked,
      };
    },
  },
  {
    id: BLUEPRINT_SYNC,
    useLocale: true,
    label: "common:blueprint_sync",
    perm: "FeatureFlag:AddOnForBluePrintSync",
    isConfigurable: false,
    addOnType: ORGANIZATION_LEVEL,
    enabledWithPlans: [
      PRO_PILOT,
      PRO,
      ESSENTIALS,
      ESSENTIALS_PILOT,
      ULTIMATE_PILOT,
      ULTIMATE,
      ADVANCE_PILOT,
      ADVANCE,
    ],
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: BLUEPRINT_SYNC,
    },
    needsConfiguration: false, // Indicating that this add-on needs additional configuration if pre-enabled due to plan
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const isInitialFetch = params.isInitialFetch || false;
      const data = await getInternalToolGenericData({
        variables,
        isInitialFetch,
      });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      // Show checked if both the configuration and consumption are enabled
      const isChecked = _.some(queryData, org_data => {
        return _.isEqual(org_data.value, true);
      });

      return {
        checked: isChecked,
      };
    },
  },
  {
    id: ROSTER_SYNC,
    useLocale: true,
    label: "integration:roster_sync",
    description: "Manage access for roster sync",
    isConfigurable: true,
    addOnType: ORGANIZATION_LEVEL,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: ROSTER_SYNC,
    },
    needsConfiguration: true,
    perm: ENABLE_ADD_ON_ROSTER_SYNC,
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );
      const isChecked = _.get(queryData, "showSyncLinkV2", false);

      return { checked: isChecked };
    },
  },
  {
    id: TIMETABLE_SYNC,
    useLocale: true,
    label: "integration:timetable_sync",
    description: "Manage access for timetable sync",
    isConfigurable: true,
    addOnType: ORGANIZATION_LEVEL,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: TIMETABLE_SYNC,
    },
    needsConfiguration: true,
    perm: ENABLE_ADD_ON_TIMETABLE_ATTENDANCE_SYNC,
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      const isChecked = _.get(queryData, "syncModule", false);

      return { checked: isChecked };
    },
  },
  {
    id: ATTENDANCE_SYNC,
    useLocale: true,
    label: "integration:attendance_sync",
    description: "Manage access for attendance sync",
    isConfigurable: true,
    addOnType: ORGANIZATION_LEVEL,
    addOnsParameters: {
      apiType: ADD_ON_MANAGER,
      addOnType: ATTENDANCE_SYNC,
    },
    needsConfiguration: true,
    perm: ENABLE_ADD_ON_TIMETABLE_ATTENDANCE_SYNC,
    getAddOnStatusFn: async params => {
      const { variables } = getVariablesForAddOnStatus(params);
      const data = await getInternalToolGenericData({ variables });

      const queryData = _.get(
        data,
        "platform.internalToolsGenericQuery.data",
        []
      );

      const isChecked = _.get(queryData, "syncModule", false);

      return { checked: isChecked };
    },
  },
];

export const checkAddOnStatusForConfigValues = async ({
  filters,
  isInitialFetch = false,
}) => {
  const queryData = await readAddOnStatusForConfigValuesFromCache({ filters });
  if (_.isEmpty(queryData) || isInitialFetch) {
    const data = await fetchAddOnStatusForConfigValues({ filters });
    return data;
  }
  return queryData;
};

export const fetchAddOnStatusForConfigValues = async ({ filters }) => {
  const { data } = await client.query({
    query: getConfigurationValuesQuery,
    variables: { filters },
    fetchPolicy: "network-only",
    context: { useHttpLink: true },
  });
  return data;
};

export const readAddOnStatusForConfigValuesFromCache = ({ filters }) => {
  try {
    const queryData = client.readQuery({
      query: getConfigurationValuesQuery,
      variables: { filters },
      fetchPolicy: "network-only",
    });
    return queryData;
  } catch (e) {
    console.error(e);
    return {};
  }
};

export const checkIfAuthAndEvalEnabled = async ({
  variables,
  isInitialFetch = false,
}) => {
  const queryData = await readIfAuthAndEvalEnabledFromCache({ variables });
  if (_.isEmpty(queryData) || isInitialFetch) {
    const { data } = await client.query({
      query: getInternalToolGenericDataQuery,
      variables,
      fetchPolicy: "network-only",
      context: { useHttpLink: true },
    });
    return data;
  }
  return queryData;
};

const readIfAuthAndEvalEnabledFromCache = async ({ variables }) => {
  try {
    const queryData = await client.readQuery({
      query: getInternalToolGenericDataQuery,
      variables,
    });
    return queryData;
  } catch (e) {
    return {};
  }
};

export const getInternalToolGenericData = async ({
  variables,
  isInitialFetch = false,
}) => {
  const queryData = await getInternalToolGenericDataFromCache({ variables });
  if (_.isEmpty(queryData) || isInitialFetch) {
    const { data } = await client.query({
      query: getInternalToolGenericDataQuery,
      variables,
      fetchPolicy: "network-only",
      context: { useHttpLink: true },
    });
    return data;
  }
  return queryData;
};

export const getInternalToolGenericDataFromCache = async ({ variables }) => {
  try {
    const queryData = await client.readQuery({
      query: getInternalToolGenericDataQuery,
      variables,
    });
    return queryData;
  } catch (e) {
    return {};
  }
};

export const toggleAddOnFn = async ({ variables }) => {
  try {
    await client.mutate({
      mutation: addConfigurationValuesMutationV2,
      variables,
    });
  } catch (e) {
    console.error(e);
  }
};

export const getOrgRegionId = ({ orgRegion }) => {
  const regionId = _.find(REGION_FILTER_OPTIONS, {
    label: REGION_NAME_BY_REGION[orgRegion],
  })?.value;
  return regionId;
};

export const getVariablesForAddOnStatus = params => {
  const { addOnId, orgId, addOnsParameters } = params;
  const { apiType, addOnType, queryType } = addOnsParameters || {};

  switch (addOnId) {
    case WORKRAMP_ACADEMY: {
      return {
        filters: {
          configurationIds: addOnsParameters?.configurationId,
          searchText: orgId,
        },
      };
    }
    case AUTH_AND_EVAL:
    case AUTH_AND_EVAL_V2: {
      return {
        variables: {
          input: {
            apiType: addOnsParameters?.apiType,
            params: {
              organizationId: orgId,
            },
          },
        },
      };
    }
    case AI_TUTOR: {
      return {
        variables: {
          input: {
            apiType: addOnsParameters?.apiType,
            params: {
              organizationId: orgId,
              addOnType: AI_TUTOR,
            },
          },
        },
      };
    }
    case TODDLE_AI: {
      return {
        variables: {
          input: {
            apiType: addOnsParameters?.apiType,
            params: {
              organizationId: orgId,
              addOnType: TODDLE_AI,
            },
          },
        },
      };
    }
    case WORKSHEET: {
      return {
        variables: {
          input: {
            apiType: addOnsParameters?.apiType,
            params: {
              organizationId: orgId,
              addOnType: WORKSHEET,
            },
          },
        },
      };
    }
    case WORKSHEET_AI: {
      return {
        variables: {
          input: {
            apiType: addOnsParameters?.apiType,
            params: {
              organizationId: orgId,
              addOnType: WORKSHEET_AI,
            },
          },
        },
      };
    }
    case AI_GRADING: {
      return {
        variables: {
          input: {
            apiType: addOnsParameters?.apiType,
            params: {
              organizationId: orgId,
              addOnType: AI_GRADING,
            },
          },
        },
      };
    }
    case IMPORT_UNIT_FROM_OTHER_CURRICULUM: {
      return {
        variables: {
          input: {
            apiType: addOnsParameters?.apiType,
            params: {
              organizationId: orgId,
              addOnType: IMPORT_UNIT_FROM_OTHER_CURRICULUM,
            },
          },
        },
      };
    }
    case UNIT_PLANNING_AI: {
      return {
        variables: {
          input: {
            apiType: addOnsParameters?.apiType,
            params: {
              organizationId: orgId,
              addOnType: UNIT_PLANNING_AI,
            },
          },
        },
      };
    }
    case TODDLE_LIBRARY: {
      return {
        variables: {
          input: {
            apiType,
            params: {
              organizationId: orgId,
              addOnType,
              queryType,
            },
          },
        },
      };
    }
    case ACADEMIC_YEAR_TRANSITION: {
      return {
        variables: {
          input: {
            apiType,
            params: {
              organizationId: orgId,
              addOnType,
            },
          },
        },
      };
    }
    case ACTIVITY_TRACKER: {
      return {
        variables: {
          input: {
            apiType: addOnsParameters?.apiType,
            params: {
              organizationId: orgId,
              addOnType: ACTIVITY_TRACKER,
            },
          },
        },
      };
    }
    case BEHAVIOUR_MANAGEMENT: {
      return {
        variables: {
          input: {
            apiType,
            params: {
              organizationId: orgId,
              addOnType,
            },
          },
        },
      };
    }
    case STUDENT_PRONOUNS: {
      return {
        variables: {
          input: {
            apiType,
            params: {
              organizationId: orgId,
              addOnType,
            },
          },
        },
      };
    }
    case IN_APP_TRANSLATION: {
      return {
        variables: {
          input: {
            apiType: addOnsParameters?.apiType,
            params: {
              organizationId: orgId,
              addOnType: IN_APP_TRANSLATION,
            },
          },
        },
      };
    }
    case DP_1_AND_DP_2_STUDENTS: {
      return {
        variables: {
          input: {
            apiType,
            params: {
              organizationId: orgId,
              addOnType,
            },
          },
        },
      };
    }
    case IBIS_REGISTRATION_REVAMP: {
      return {
        variables: {
          input: {
            apiType,
            params: {
              organizationId: orgId,
              addOnType,
            },
          },
        },
      };
    }
    case BLUEPRINT_SYNC: {
      return {
        variables: {
          input: {
            apiType,
            params: {
              organizationId: orgId,
              addOnType,
            },
          },
        },
      };
    }
    case ROSTER_SYNC: {
      return {
        variables: {
          input: {
            apiType,
            params: {
              organizationId: orgId,
              addOnType,
            },
          },
        },
      };
    }
    case TIMETABLE_SYNC: {
      return {
        variables: {
          input: {
            apiType,
            params: {
              organizationId: orgId,
              addOnType,
            },
          },
        },
      };
    }
    case ATTENDANCE_SYNC: {
      return {
        variables: {
          input: {
            apiType,
            params: {
              organizationId: orgId,
              addOnType,
            },
          },
        },
      };
    }
    case ADD_ADMIN_TO_ALL_CLASSES: {
      return {
        variables: {
          input: {
            apiType,
            params: {
              organizationId: orgId,
              addOnType,
            },
          },
        },
      };
    }
    case TODDLE_ANALYTICS: {
      return {
        variables: {
          input: {
            apiType,
            params: {
              organizationId: orgId,
              addOnType,
            },
          },
        },
      };
    }
    case CROSS_CURRICULUM_STUDENTS: {
      return {
        variables: {
          input: {
            apiType,
            params: {
              organizationId: orgId,
              addOnType,
            },
          },
        },
      };
    }
    case ROLES_AND_PERMISSIONS: {
      return {
        variables: {
          input: {
            apiType,
            params: {
              organizationId: orgId,
              addOnType,
            },
          },
        },
      };
    }
  }
};

export const getVariablesForToggleAddOn = params => {
  const { addOnId, addOnsParameters, orgId, orgRegion, toggleValue } = params;
  switch (addOnId) {
    case WORKRAMP_ACADEMY: {
      return {
        variables: {
          input: {
            id: addOnsParameters?.configurationId,
            valueType: addOnsParameters?.valueType,
            value: String(toggleValue),
            regionId: getOrgRegionId({ orgRegion }),
            entityGroups: [
              [{ entityId: orgId, entityType: addOnsParameters?.entityType }],
            ],
          },
        },
      };
    }
  }
};

export const getWorkrampAcademyStatusForOrg = async params => {
  const { addOnsParameters, orgId, addOnId, isInitialFetch = false } = params;

  const { filters } = getVariablesForAddOnStatus({
    addOnsParameters,
    orgId,
    addOnId,
  });
  const data = await checkAddOnStatusForConfigValues({
    filters,
    isInitialFetch,
  });
  const isChecked = _.get(
    data,
    "platform.getConfigurationValues[0].values[0].value",
    false
  );
  return {
    checked: isChecked === "true" ? true : false,
  };
};

export const enableWorkrampAcademyForOrg = params => {
  return async (dispatch, getState) => {
    const {
      addOnsParameters,
      orgId,
      orgRegion,
      addOnId,
      value,
      dropdownQueryVariables,
    } = params;
    const { variables } = getVariablesForToggleAddOn({
      addOnId,
      addOnsParameters: dropdownQueryVariables
        ? dropdownQueryVariables
        : addOnsParameters,
      orgId,
      orgRegion,
      toggleValue: value,
    });

    const { filters } = getVariablesForAddOnStatus({
      addOnsParameters,
      orgId,
      addOnId,
    });

    await toggleAddOnFn({ variables });
    await fetchAddOnStatusForConfigValues({ filters });
  };
};

const readIfAddOnEnabledFromCache = async ({ variables }) => {
  try {
    const queryData = await client.readQuery({
      query: getInternalToolGenericDataQuery,
      variables,
    });
    return queryData;
  } catch (e) {
    return {};
  }
};

export const checkIfAddOnEnabled = async ({
  variables,
  isInitialFetch = false,
}) => {
  const queryData = await readIfAddOnEnabledFromCache({ variables });
  if (_.isEmpty(queryData) || isInitialFetch) {
    const { data } = await client.query({
      query: getInternalToolGenericDataQuery,
      variables,
      fetchPolicy: "network-only",
      context: { useHttpLink: true },
    });
    return data;
  }
  return queryData;
};

export const updateAddOnStatus = ({
  orgId,
  curriculumId,
  newStatus,
  updateOn,
  addOnType,
  moduleId,
  queryVariables,
  enabledUserIds,
  disabledUserIds,
  isSingleCurriculumProgramId = false,
  additionalRefetchQueries = [],
  additionalInput = {},
  addOnSubType = "", // Can be used as a filter when there are multiple addOns within the same type and `updateOn` is ORGANIZATION
}) => {
  return async (dispatch, getState) => {
    let inputArray = [];

    switch (updateOn) {
      case CURRICULUM_PROGRAM: {
        inputArray = [
          ...inputArray,
          {
            state: newStatus ? ENABLE : DISABLE,
            ...(isSingleCurriculumProgramId
              ? {
                  curriculumId,
                }
              : {
                  curriculumProgramIds: [curriculumId],
                }),
            moduleId,
            type: updateOn,
          },
        ];
        break;
      }
      case GLOBAL: {
        inputArray = [
          ...inputArray,
          {
            state: newStatus ? ENABLE : DISABLE,
            moduleId,
            type: updateOn,
          },
        ];
        break;
      }
      case USER: {
        if (_.size(enabledUserIds)) {
          inputArray = [
            ...inputArray,
            {
              state: ENABLE,
              ...(_.isUndefined(curriculumId)
                ? {}
                : { curriculumProgramIds: [curriculumId] }),
              userIds: enabledUserIds,
              moduleId,
              type: updateOn,
            },
          ];
        }
        if (_.size(disabledUserIds)) {
          inputArray = [
            ...inputArray,
            {
              state: DISABLE,
              ...(_.isUndefined(curriculumId)
                ? {}
                : { curriculumProgramIds: [curriculumId] }),
              userIds: disabledUserIds,
              moduleId,
              type: updateOn,
            },
          ];
        }
        break;
      }
      case ORGANIZATION: {
        // not an array is required but an object still since its array everywhere else we are
        // keeping the name as array
        inputArray = {
          permissionValue: newStatus,
          ...(addOnSubType ? { addOnSubType } : {}),
        };
        break;
      }
    }

    if (_.isEqual(addOnType, IBIS_REGISTRATION_REVAMP)) {
      inputArray = {
        permissionValue: newStatus,
        ...additionalInput,
      };
    }

    if (_.isEqual(addOnType, ROSTER_SYNC)) {
      inputArray = {
        permissionValue: newStatus,
        addOnSubType,
        ...(_.size(enabledUserIds) ? { userIds: enabledUserIds } : {}),
      };
    }
    if (_.isEqual(addOnType, ATTENDANCE_SYNC)) {
      inputArray = {
        permissionValue: newStatus,
        addOnSubType,
        ...additionalInput,
      };
    }
    if (_.isEqual(addOnType, TIMETABLE_SYNC)) {
      inputArray = {
        permissionValue: newStatus,
        addOnSubType,
        ...(!_.isEmpty(additionalInput) ? { content: additionalInput } : {}),
      };
    }

    if (_.isEqual(addOnType, TODDLE_ANALYTICS)) {
      // An array is not required for toddle analytics.
      // toddle analytics mutation input is different from all the above cases
      inputArray = newStatus;
    }

    try {
      await client.mutate({
        mutation: internalToolsGenericMutation,
        variables: {
          apiType: ADD_ON_MANAGER,
          params: {
            organizationId: orgId,
            addOnType,
            input: inputArray,
          },
        },
        refetchQueries: [
          {
            query: getInternalToolGenericDataQuery,
            variables: queryVariables,
          },
          ...additionalRefetchQueries,
        ],
        awaitRefetchQueries: true,
      });
    } catch (e) {
      console.error(e);
    }
  };
};
